// #tag: MOBILE FLOATING DOCK COMPONENT
// #purpose: Renders an iOS/macOS styled floating pill navigation bar with scroll detection, layout animations, and haptic spring motion.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence as FramerAnimatePresence, LayoutGroup, motion, type AnimatePresenceProps } from 'framer-motion';
import type { NavItem } from '../../config/navigation';

const AnimatePresence = FramerAnimatePresence as React.FC<React.PropsWithChildren<AnimatePresenceProps>>;

type FloatingDockProps = {
  items: NavItem[];
  activePath: string | null;
  onNavigate: (path: string) => void;
};

type ScrollState = 'rest' | 'lifted' | 'hidden';

const DOCK_TRANSITION = { duration: 0.28, ease: [0.22, 0.8, 0.24, 1] as const };

export function FloatingDock({ items, activePath, onNavigate }: FloatingDockProps) {
  const [scrollState, setScrollState] = useState<ScrollState>('rest');
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    lastScrollY.current = window.scrollY;
    const onScroll = () => {
      if (ticking.current) return;
      ticking.current = true;
      window.requestAnimationFrame(() => {
        const currentY = window.scrollY;
        const delta = currentY - lastScrollY.current;
        if (delta > 8 && currentY > 48) setScrollState('hidden');
        else if (delta < -4) setScrollState('lifted');
        else if (Math.abs(delta) <= 2) setScrollState('rest');
        lastScrollY.current = currentY;
        ticking.current = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavigate = useCallback(
    (path: string) => onNavigate(path),
    [onNavigate],
  );

  return (
    <nav
      className={`floating-dock ${scrollState !== 'rest' ? `is-${scrollState}` : ''}`}
      aria-label="Mobile primary navigation"
    >
      <div className="floating-dock-track">
        <LayoutGroup id="floating-dock">
          {items.map((item) => {
            const isActive = activePath === item.path;
            const Icon = item.icon;
            return (
              <button
                key={item.path}
                type="button"
                className={`floating-dock-item ${isActive ? 'is-active' : ''}`}
                aria-current={isActive ? 'page' : undefined}
                aria-label={item.label}
                onClick={() => handleNavigate(item.path)}
              >
                {isActive ? (
                  <motion.span
                    className="floating-dock-pill"
                    layoutId="dock-active-pill"
                    transition={DOCK_TRANSITION}
                  />
                ) : null}
                <span className="floating-dock-icon-wrap">
                  <motion.span
                    className="floating-dock-icon"
                    animate={{ scale: isActive ? 1 : 0.9, rotate: isActive ? 5 : 0 }}
                    transition={DOCK_TRANSITION}
                  >
                    <Icon size={24} aria-hidden="true" />
                  </motion.span>
                </span>
                <AnimatePresence initial={false}>
                  {isActive ? (
                    <motion.span
                      key="label"
                      className="floating-dock-label"
                      initial={{ opacity: 0, maxWidth: 0 }}
                      animate={{ opacity: 1, maxWidth: 120 }}
                      exit={{ opacity: 0, maxWidth: 0 }}
                      transition={DOCK_TRANSITION}
                    >
                      {item.short}
                    </motion.span>
                  ) : null}
                </AnimatePresence>
              </button>
            );
          })}
        </LayoutGroup>
      </div>
    </nav>
  );
}
